import { definedMap, reduceTwo } from "@glideapps/ts-necessities";
import { Doc } from "../../../common/docUrl";

import { getLocalizedString } from "../../components/localization";
import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import { ValueProducer } from "../../components/types";
import { getFeatureSetting } from "../../feature-settings";
import { makeText, makeValidationFlagForProperty, setColumnInRowForEditComponent } from "../builder-utils";
import { makePlaceholderPropertyDescriptor, makeTextPropertyDescriptor } from "../components/descriptor-utils";
import {
    ActionPropertyDescriptor,
    ComponentBuilder,
    ComponentConfiguratorContext,
    ComponentSpecialCaseDescriptor,
    PropertyDescriptor,
    PropertyTableGetter,
} from "../components/index";
import { ColumnPropertyFlag, ColumnPropertyHandler, isRequiredPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { getColumnProperty } from "../components/property-utils";
import {
    ArrayContentDescription,
    ArrayScreenFormat,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    TableGlideType,
    getPrimitiveNonHiddenColumns,
} from "../description";
import { titleProperties } from "../description-utils";
import { SerializedElementBuilder } from "../serialized-component";
import { ArrayContentHandlerBase, InlineListBuild } from "./array-content";
import { makeDescriptionPropertyDescriptor, makeEndDateTimePropertyDescriptor } from "./calendar-screen";

interface EventPickerArrayContentDescription extends ArrayContentDescription {
    readonly startProperty: PropertyDescription | undefined;
    readonly endProperty: PropertyDescription | undefined;
    readonly titleProperty: PropertyDescription | undefined;
    readonly descriptionProperty: PropertyDescription | undefined;

    readonly editStartProperty: PropertyDescription;
    readonly editEndProperty: PropertyDescription;

    readonly screenTitle: PropertyDescription | undefined;
    readonly placeholder: PropertyDescription | undefined;

    readonly isRequired: PropertyDescription;
}

const editStartPropertyHandler: ColumnPropertyHandler<EventPickerArrayContentDescription> = new ColumnPropertyHandler(
    "editStartProperty",
    "Event start",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp],
    undefined,
    undefined,
    getPrimitiveNonHiddenColumns,
    "date-time",
    PropertySection.Data
);
const editEndPropertyHandler: ColumnPropertyHandler<EventPickerArrayContentDescription> = new ColumnPropertyHandler(
    "editEndProperty",
    "Event end",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp],
    undefined,
    undefined,
    getPrimitiveNonHiddenColumns,
    "date-time",
    PropertySection.Data
);

export class EventPickerArrayContentHandler extends ArrayContentHandlerBase<EventPickerArrayContentDescription> {
    constructor() {
        super(ArrayScreenFormat.EventPicker);
    }

    public get helpPath(): Doc {
        return "eventPicker";
    }

    public get isEditor(): boolean {
        return true;
    }

    public get supportsEmptyArrays(): boolean {
        return true;
    }

    public get specialCaseDescriptor(): ComponentSpecialCaseDescriptor | undefined {
        if (!getFeatureSetting("eventPicker")) return undefined;

        return {
            name: "Event Picker",
            analyticsName: "event-picker",
            description: "Pick start and end times for an event",
            img: "componentDatePicker",
            group: "Pickers",
            appKinds: AppKind.App,
        };
    }

    public needsCaption(_desc: EventPickerArrayContentDescription | undefined): PropertySection | undefined {
        return PropertySection.Design;
    }

    public getDescriptiveName(
        _desc: EventPickerArrayContentDescription,
        caption: string | undefined
    ): [string, string] | undefined {
        if (caption === undefined) return undefined;
        return ["Event Picker", caption];
    }

    public get needsOutputContext(): boolean {
        return true;
    }

    public get sourcePropertyLabel(): readonly [string, PropertySection] {
        return ["Data", PropertySection.Events];
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: EventPickerArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        _searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        if (inlineListBuild === undefined) return b.emptyUI;
        const { tables, inputContext, outputContext, mutatingScreenKind } = inlineListBuild;
        if (outputContext === undefined) return b.emptyUI;

        const startPropertyName = getColumnProperty(desc.startProperty);
        const endPropertyName = getColumnProperty(desc.endProperty);
        const titlePropertyName = getColumnProperty(desc.titleProperty);
        const descriptionPropertyName = getColumnProperty(desc.descriptionProperty);

        const editStartPropertyName = editStartPropertyHandler.getColumnName(desc);
        const editEndPropertyName = editEndPropertyHandler.getColumnName(desc);
        if (editStartPropertyName === undefined || editEndPropertyName === undefined) return b.emptyUI;

        const editStartValue = b.getProperty(editStartPropertyName, outputContext, table);
        const editEndValue = b.getProperty(editEndPropertyName, outputContext, table);
        const startSetColumns = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            editStartPropertyName,
            editStartValue,
            undefined,
            tables.output,
            undefined,
            outputContext,
            { withDiode: false }
        );
        const endSetColumns = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            editEndPropertyName,
            editEndValue,
            undefined,
            tables.output,
            undefined,
            outputContext,
            { withDiode: false }
        );
        if (startSetColumns === undefined || endSetColumns === undefined) return b.emptyUI;

        const {
            lets: editStartLets,
            valueToModify: editStartValueToModify,
            action: editStartAction,
            isEnabled: editStartIsEnabled,
        } = startSetColumns;
        const {
            lets: editEndLets,
            valueToModify: editEndValueToModify,
            action: editEndAction,
            isEnabled: editEndIsEnabled,
        } = endSetColumns;

        const isEnabled = reduceTwo(editStartIsEnabled, editEndIsEnabled, (x, y) => b.and(x, y));

        const action = reduceTwo(editStartAction, editEndAction, (x, y) => b.sequence([x, y]));

        const screenTitle = makeText(builder, desc.screenTitle, tables.input, true, true, false, false, inputContext);
        const isRequired = isRequiredPropertyHandler.getSwitch(desc);

        let events: SerializedComponent | undefined;
        if (startPropertyName !== undefined) {
            events = b.map(
                arrayData,
                b.object({
                    start: b.parseUserDateTime(b.getProperty(startPropertyName, b.getContext, table)),
                    end: definedMap(endPropertyName, n => b.parseUserDateTime(b.getProperty(n, b.getContext, table))),
                    title: definedMap(titlePropertyName, n => b.getProperty(n, b.getContext, table)),
                    description: definedMap(descriptionPropertyName, n => b.getProperty(n, b.getContext, table)),
                })
            );
        }

        const lets = [...editStartLets, ...editEndLets];
        const onClear = [b.set(editStartValueToModify, b.constant("")), b.set(editEndValueToModify, b.constant(""))];
        if (action !== undefined) {
            lets.push(["setAction", action]);
            onClear.push(b.getVariable("setAction"));
        }

        return b.let(
            lets,
            b.ui("EventPickerButton", {
                isEnabled,
                eventStart: editStartValueToModify,
                eventEnd: editEndValueToModify,
                onClick: builder.makePushEventPickerScreen(
                    editStartValueToModify,
                    editEndValueToModify,
                    events,
                    screenTitle ?? b.localizedString("newEvent"),
                    b.constant(isRequired),
                    action
                ),
                onClear: b.sequence(onClear),
                title: caption,
                placeholder:
                    makeText(builder, desc.placeholder, tables.input, true, true, false, false, inputContext) ??
                    b.localizedString("pickADateAndTime"),
                isRequired: b.constant(isRequired),
            })
        );
    }

    public getContentPropertyDescriptors<T extends EventPickerArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        arrayVP: ValueProducer | undefined
    ): readonly PropertyDescriptor[] {
        const properties = [
            editStartPropertyHandler,
            editEndPropertyHandler,
            makeTextPropertyDescriptor(
                "screenTitle",
                "Screen Title",
                getLocalizedString("newEvent"),
                false,
                mutatingScreenKind,
                {
                    preferredType: "string",
                    searchable: false,
                    emptyByDefault: true,
                    propertySection: PropertySection.Design,
                }
            ),
            makePlaceholderPropertyDescriptor(
                mutatingScreenKind,
                PropertySection.Design,
                getLocalizedString("pickADateAndTime")
            ),
        ];
        if (mutatingScreenKind !== undefined) {
            properties.push(isRequiredPropertyHandler);
        }
        properties.push(
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                arrayVP
            ),
            {
                kind: PropertyKind.Column,
                property: { name: "startProperty" },
                section: PropertySection.Events,
                label: "Start",
                required: false,
                editable: true,
                searchable: false,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: ["start", "begin", "event start", "event end"],
                preferredType: "date-time",
            },
            makeEndDateTimePropertyDescriptor(getPropertyTable, PropertySection.Events),
            {
                kind: PropertyKind.Column,
                property: { name: "titleProperty" },
                section: PropertySection.Events,
                label: "Title",
                required: false,
                editable: true,
                searchable: false,
                getIndirectTable: getPropertyTable,
                getAllowedColumns: getPrimitiveNonHiddenColumns,
                preferredNames: titleProperties,
                preferredType: "string",
            },
            makeDescriptionPropertyDescriptor(getPropertyTable, PropertySection.Events)
        );
        return properties;
    }

    public getActionDescriptors<
        T extends EventPickerArrayContentDescription
    >(): readonly ActionPropertyDescriptor<T>[] {
        return [];
    }

    public getBasicSearchProperties(): readonly string[] {
        return [];
    }

    public needValidation(desc: EventPickerArrayContentDescription): boolean {
        return isRequiredPropertyHandler.getSwitch(desc);
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: EventPickerArrayContentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        const startFlag = makeValidationFlagForProperty(
            b,
            outputContext,
            editStartPropertyHandler.getColumnName(desc),
            table
        );
        const endFlag = makeValidationFlagForProperty(
            b,
            outputContext,
            editEndPropertyHandler.getColumnName(desc),
            table
        );
        return reduceTwo(startFlag, endFlag, (x, y) => b.and(x, y));
    }
}
